import React, { useState, useEffect, useRef, useContext } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';
import './gpsUnlockButton.css';

const MOVEMENT_THRESHOLD_METERS = 1; // Only update if moved > 1m

const GpsUnlockButton = ({
  clueId,
  clueLocation,
  radiusMeters,
  disabled = false,
  unlockedClues = [],
}) => {
  const { player } = useContext(AuthContext);
  const [status, setStatus] = useState('');
  const [distance, setDistance] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [animateDistance, setAnimateDistance] = useState(false);
  const [playerCoords, setPlayerCoords] = useState(null);
  const lastCoordsRef = useRef(null);
  const lastMovementTime = useRef(Date.now());
  const pollingTimeoutRef = useRef(null);
  const mobileView = useMobileView();
  const gpsUnlockRef = useRef(null);
  const [animateLocation, setAnimateLocation] = useState(false);

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    gpsUnlockRef,
    mobileView,
    '+5 JGK Location Unlocked!',
    1000,
    'gpsUnlock',
  );

  const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // meters
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const playUnlockSound = () => {
    const audio = new Audio(
      'https://res.cloudinary.com/joegalaxy/video/upload/v1752624122/Futuristic_HUD_Sound_Design_Blake_Sanchez_touovl.mp3',
    );
    audio.currentTime = 0;
    audio.play();
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 1000); // Play only 1 second
  };

  const vibrateOnUnlock = () => {
    if (window.navigator.vibrate) {
      window.navigator.vibrate(300);
    }
  };

  const unlockClue = async (userLocation) => {
    try {
      const res = await axiosConn.post(
        `/clues/unlock/gps/${clueId}`,
        { latitude: userLocation.latitude, longitude: userLocation.longitude },
        { headers: { Authorization: `Bearer ${player?.token}` } },
      );
      setStatus(res.data.message);
      setIsUnlocked(true);
      showConfettiText(); // trigger confetti
      playUnlockSound(); // play sound
      vibrateOnUnlock();
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    } catch (err) {
      setStatus(err.response?.data?.message || 'Error unlocking clue');
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser');
      return;
    }
    if (disabled) return;

    let mounted = true;

    const pollLocation = () => {
      if (!mounted) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const playerLat = pos.coords.latitude;
          const playerLon = pos.coords.longitude;
          setPlayerCoords({ latitude: playerLat, longitude: playerLon });
          setAnimateLocation(true);
          setTimeout(() => setAnimateLocation(false), 400);

          // Only update if moved more than threshold
          let moved = MOVEMENT_THRESHOLD_METERS;
          if (lastCoordsRef.current) {
            moved = getDistanceMeters(
              lastCoordsRef.current.latitude,
              lastCoordsRef.current.longitude,
              playerLat,
              playerLon,
            );
          }
          if (!lastCoordsRef.current || moved >= MOVEMENT_THRESHOLD_METERS) {
            lastCoordsRef.current = {
              latitude: playerLat,
              longitude: playerLon,
            };
            lastMovementTime.current = Date.now();
          }

          const [clueLon, clueLat] = clueLocation;
          const dist = getDistanceMeters(
            playerLat,
            playerLon,
            clueLat,
            clueLon,
          );
          setDistance(dist);

          if (!isUnlocked && dist <= radiusMeters) {
            unlockClue({ latitude: playerLat, longitude: playerLon });
          }

          // Adaptive interval: 2s if moved in last 30s, else 10s
          const now = Date.now();
          const interval =
            now - lastMovementTime.current < 30000 ? 2000 : 10000;
          pollingTimeoutRef.current = setTimeout(pollLocation, interval);
        },
        (err) => {
          setStatus('Unable to retrieve your location');
          pollingTimeoutRef.current = setTimeout(pollLocation, 10000); // Retry in 10s
        },
        { enableHighAccuracy: true },
      );
    };

    pollLocation();

    return () => {
      mounted = false;
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [clueLocation, radiusMeters, isUnlocked, player?.token, disabled]);

  useEffect(() => {
    if (distance !== null) {
      setAnimateDistance(true);
      const timeout = setTimeout(() => setAnimateDistance(false), 400);
      return () => clearTimeout(timeout);
    }
  }, [distance]);

  const progressPercent =
    distance !== null ? Math.min(100, (1 - distance / radiusMeters) * 100) : 0;

  const getDistanceColor = (distance) => {
    if (distance <= radiusMeters) return '#16a34a'; // green
    if (distance < radiusMeters * 2) return '#f59e42'; // orange
    return '#e11d48'; // red
  };

  if (disabled) {
    return (
      <div
        ref={gpsUnlockRef}
        className="text-center"
        style={{
          fontSize: 20,
          color: '#ef4444',
          marginTop: 8,
          fontWeight: 600,
          animation: 'pop 0.7s',
          letterSpacing: 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span role="img" aria-label="celebrate" style={{ fontSize: 38 }}>
          🎉
        </span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>
          This location clue is already unlocked!
        </span>
        <ConfettiTextComponent />
        <span
          className="badge"
          style={{
            background: '#e5e7eb',
            color: '#ef4444',
            fontWeight: 700,
            fontSize: 15,
            borderRadius: 10,
            marginTop: 8,
            boxShadow: '0 2px 8px #ef444422',
            letterSpacing: 0.5,
            animation: 'pop 0.7s',
          }}
        >
          <span role="img" aria-label="check">
            ✔️
          </span>{' '}
          Solved!
        </span>
        <style>
          {`
            @keyframes pop {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }
  console.log('Distance:', distance, 'meters');
  return (
    <div className="text-center">
      {/* Pulsing GPS Icon */}
      <div style={{ marginBottom: 8 }}>
        <span
          style={{
            fontSize: 28,
            color: '#2563eb',
            animation: 'gpsPulse 1.2s infinite alternate',
            display: 'inline-block',
            verticalAlign: 'middle',
            transition: 'all 0.3s',
          }}
          role="img"
          aria-label="GPS"
        >
          📡
        </span>
        <style>
          {`
            @keyframes gpsPulse {
              0% { transform: scale(1); opacity: 0.7; }
              100% { transform: scale(1.15); opacity: 1; }
            }
            .progress-bar {
              transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
            }
            @keyframes pop {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
      <button
        className="btn mb-2 fw-bold btn-primary"
        disabled
        style={{
          cursor: 'not-allowed',
          fontSize: 17,
          letterSpacing: 0.5,
          boxShadow: '0 2px 8px #2563eb22',
          transition: 'all 0.3s',
        }}
      >
        Real-time GPS Tracking Active
      </button>
      {playerCoords && (
        <div
          className={`small text-muted ${
            animateLocation ? 'location-animate' : ''
          }`}
          style={{ marginTop: 4 }}
        >
          <strong>Your Location:</strong> {playerCoords.latitude.toFixed(5)},{' '}
          {playerCoords.longitude.toFixed(5)}
        </div>
      )}
      {distance !== null && (
        <div
          className={`fw-bold ${animateDistance ? 'distance-animate' : ''}`}
          style={{ color: getDistanceColor(distance), fontSize: 18 }}
        >
          Distance: {distance.toFixed(2)} meters
        </div>
      )}
      <div className="progress my-2" style={{ height: 8 }}>
        <div
          className={`progress-bar ${
            distance <= radiusMeters ? 'bg-success' : 'bg-warning'
          }`}
          role="progressbar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      {/* Tooltip/Hint */}
      {distance !== null && distance > radiusMeters && (
        <div className="small text-muted" style={{ marginTop: 4 }}>
          Move closer to the clue location to unlock!
        </div>
      )}
      {/* Animated Emoji Feedback */}
      {isUnlocked && (
        <div
          style={{
            fontSize: 36,
            margin: '12px 0',
            animation: 'pop 0.7s',
            color: '#16a34a',
            fontWeight: 700,
          }}
        >
          🎉 Unlocked!
        </div>
      )}
      {status && <p className="text-muted small mt-2">{status}</p>}
    </div>
  );
};

export default GpsUnlockButton;
